import { useEffect, useState } from "react";
import { Loader2, Plus, Copy, Ticket, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const STATUS_CLS = {
  pending: "text-amber-300 border-amber-500/40",
  approved: "text-emerald-300 border-emerald-500/40",
  rejected: "text-red-300 border-red-500/40",
};

const FIELD = "bg-transparent border border-aura-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-white/40 outline-none transition-colors";

export function CreatorCodes() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [percent, setPercent] = useState(10);
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    api.get("/creator/discount-requests").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/creator/discount-requests", { percent: Number(percent), code: code || null });
      setRows((arr) => [data, ...arr]);
      setCode("");
      setPercent(10);
      toast.success(t("codes.created"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = (c) => {
    const link = `${window.location.origin}/?code=${c}#prodotto`;
    navigator.clipboard.writeText(link);
    toast.success(t("codes.copied"));
  };

  return (
    <div className="rounded-2xl border border-aura-border bg-aura-surface p-6 md:p-8" data-testid="creator-codes">
      <div className="flex items-center gap-2 mb-2"><Ticket size={18} className="text-zinc-400" /><h2 className="font-display text-lg font-semibold text-white">{t("codes.title")}</h2></div>
      <p className="text-sm text-zinc-500 mb-6 max-w-2xl">{t("codes.subtitle")}</p>

      <form onSubmit={submit} className="flex flex-col sm:flex-row sm:items-end gap-3 mb-8" data-testid="code-request-form">
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500">{t("codes.percent")}</label>
          <input data-testid="code-percent-input" type="number" min={5} max={30} value={percent} onChange={(e) => setPercent(e.target.value)} className={`${FIELD} w-28`} required />
          <span className="text-[11px] text-zinc-600">{t("codes.rangeHint")}</span>
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500">{t("codes.customCode")}</label>
          <input data-testid="code-custom-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t("codes.codePlaceholder")} className={`${FIELD} w-full uppercase`} />
        </div>
        <button data-testid="code-submit-btn" type="submit" disabled={submitting}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-semibold text-sm px-6 py-2.5 hover:bg-zinc-200 active:scale-95 transition-[background-color,transform] duration-300 disabled:opacity-60">
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} {submitting ? t("codes.submitting") : t("codes.submit")}
        </button>
      </form>

      {loading ? (
        <div className="py-8 text-center text-zinc-500"><Loader2 className="animate-spin inline" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-zinc-500">{t("codes.empty")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="creator-codes-table">
            <thead>
              <tr className="border-b border-white/10 text-start text-xs uppercase tracking-wider text-zinc-500">
                <th className="px-3 py-3 font-medium text-start">{t("codes.colCode")}</th>
                <th className="px-3 py-3 font-medium text-start">{t("codes.colPercent")}</th>
                <th className="px-3 py-3 font-medium text-start">{t("codes.colStatus")}</th>
                <th className="px-3 py-3 font-medium text-start">{t("codes.colUses")}</th>
                <th className="px-3 py-3 font-medium text-start">{t("codes.colSales")}</th>
                <th className="px-3 py-3 font-medium text-start">{t("codes.colCommission")}</th>
                <th className="px-3 py-3 font-medium text-end"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`creator-code-row-${r.id}`} className="border-b border-white/5">
                  <td className="px-3 py-4 font-mono text-white">{r.code}</td>
                  <td className="px-3 py-4 text-zinc-300">{r.percent}%</td>
                  <td className="px-3 py-4"><span className={`inline-flex items-center rounded-full border ${STATUS_CLS[r.status]} bg-white/[0.03] px-3 py-1 text-xs font-medium`}>{t(`codes.${r.status}`)}</span></td>
                  <td className="px-3 py-4 text-zinc-300">{r.times_used || 0}</td>
                  <td className="px-3 py-4 text-zinc-300">€{(r.total_sales || 0).toLocaleString()}</td>
                  <td className="px-3 py-4 text-zinc-300">€{(r.total_commission || 0).toLocaleString()}</td>
                  <td className="px-3 py-4 text-end">
                    {r.status === "approved" && (
                      <button data-testid={`code-copy-${r.id}`} onClick={() => copyLink(r.code)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 text-zinc-200 hover:bg-white/5 px-3 py-1.5 text-xs font-medium transition-colors">
                        <Copy size={13} /> {t("codes.copyLink")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-5 flex items-start gap-2 text-xs text-amber-300/80">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {t("codes.subtitle")}
      </p>
    </div>
  );
}
